import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { financingStageKeys } from '@brimo/shared';
import { Banner, Button, EmptyState, SearchField, Segmented, Skeleton } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { usePermissions } from '../../lib/session';
import { LockedPage } from '../../shell/ModulePages';
import { useFinancingList } from './api';
import { FinancingBoard } from './board/FinancingBoard';
import { CreateFinancingDialog } from './create/CreateFinancingDialog';
import { FilterPills } from './list/FilterPills';
import { FinancingsList } from './list/FinancingsList';
import type { ListFilters, PillKey, StageKey } from './types';
import styles from './FinancingsPage.module.css';

export interface FinancingsPageProps {
  /** `/financing/new?contactId=|dealId=`: the create dialog (Finanzierungen 04/05/10) is open above the list. */
  createOpen?: boolean | undefined;
}

function isStageFilter(value: string | null): value is StageKey | 'lost' {
  return value === 'lost' || (financingStageKeys as readonly string[]).includes(value ?? '');
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * Tools › Finanzierungen (Finanzierungen 01/02/03, 09): header with the count line, the Liste/Board switch
 * (`?view=board`, team only), the search (`?q=`), „Finanzierung anlegen"; the stage pills (`?stage=`,
 * `?withoutDeal=1`); the list or the board; the create dialog on `/financing/new`.
 */
export function FinancingsPage({ createOpen = false }: FinancingsPageProps) {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [params, setParams] = useSearchParams();
  const allowed = permissions.tool('financing') === 'ok';

  const stageParam = params.get('stage');
  const filters: ListFilters = {
    stage: isStageFilter(stageParam) ? stageParam : null,
    withoutDeal: params.get('withoutDeal') === '1',
    q: params.get('q') ?? '',
  };
  const [search, setSearch] = useState(filters.q);
  const debounced = useDebounced(search.trim(), 300);

  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  useEffect(() => {
    if (debounced !== filters.q) patch({ q: debounced || null });
  }, [debounced]);
  useEffect(() => {
    setSearch(filters.q);
  }, [filters.q]);

  const list = useFinancingList(filters, allowed);
  const first = list.data?.pages[0];
  const view = first?.canBoard && params.get('view') === 'board' ? 'board' : 'list';

  const closeCreate = useCallback(
    (created?: { id: string }) => {
      if (created) {
        void navigate(`/financing/${created.id}`);
        return;
      }
      const next = new URLSearchParams(params);
      next.delete('contactId');
      next.delete('dealId');
      void navigate({ pathname: '/financing', search: next.toString() ? `?${next.toString()}` : '' });
    },
    [navigate, params],
  );
  const openCreate = useCallback(() => {
    const next = new URLSearchParams(params);
    void navigate(`/financing/new${next.toString() ? `?${next.toString()}` : ''}`);
  }, [navigate, params]);

  if (!allowed) return <LockedPage tool="financing" />;

  const rows = list.data?.pages.flatMap((page) => page.rows) ?? [];
  const empty = first?.empty ?? null;
  const errorStatus = list.error instanceof ApiError ? list.error.status : null;

  function selectPill(key: PillKey) {
    if (key === 'without_deal') patch({ withoutDeal: filters.withoutDeal ? null : '1' });
    else if (key === 'all') patch({ stage: null });
    else patch({ stage: filters.stage === key ? null : key });
  }

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1 className={styles.title}>{first?.title ?? 'Finanzierungen'}</h1>
        {first ? <span className={styles.count}>{first.countLine}</span> : null}
        {first && !empty ? (
          <div className={styles.headRight}>
            {first.canBoard ? (
              <Segmented
                label="Ansicht"
                value={view}
                items={[
                  { value: 'list', label: 'Liste' },
                  { value: 'board', label: 'Board' },
                ]}
                onChange={(value) => patch({ view: value === 'board' ? 'board' : null })}
              />
            ) : null}
            <SearchField
              label="Finanzierungen durchsuchen"
              placeholder="Kontakt, Nr. oder Bank suchen"
              icon={false}
              className={styles.search}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {first.canCreate ? (
              <Button variant="secondary" onClick={openCreate}>
                Finanzierung anlegen
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {list.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button
              variant="destructiveFilled"
              size="small"
              loading={list.isFetching}
              onClick={() => void list.refetch()}
            >
              Erneut laden
            </Button>
          }
        >
          Finanzierungen konnten nicht geladen werden{errorStatus ? ` · Fehler ${errorStatus}` : ''}.{' '}
          {errorMessage(list.error)}
        </Banner>
      ) : null}

      {list.isPending ? (
        <div className={styles.skeleton} aria-busy="true">
          <Skeleton width={520} height={28} />
          <Skeleton variant="block" height={360} />
        </div>
      ) : null}

      {first && empty ? (
        <div className={styles.emptyWrap}>
          <EmptyState
            eyebrow={empty.eyebrow}
            title={empty.title}
            description={empty.text}
            actions={
              <>
                {first.canCreate ? <Button onClick={openCreate}>Finanzierung anlegen</Button> : null}
                <Button variant="secondary" onClick={() => void navigate('/deals')}>
                  Zu den Deals
                </Button>
              </>
            }
          />
        </div>
      ) : null}

      {first && !empty ? (
        <>
          {view === 'list' ? (
            <>
              <FilterPills pills={first.pills} onSelect={selectPill} />
              <FinancingsList
                view={first.view}
                rows={rows}
                query={filters.q}
                dimmed={list.isFetching && list.isPlaceholderData}
                hasMore={list.hasNextPage}
                loadingMore={list.isFetchingNextPage}
                onLoadMore={() => void list.fetchNextPage()}
              />
            </>
          ) : (
            <FinancingBoard q={filters.q} />
          )}
        </>
      ) : null}

      {createOpen ? (
        <CreateFinancingDialog
          contactId={params.get('contactId') ?? undefined}
          dealId={params.get('dealId') ?? undefined}
          onClose={closeCreate}
        />
      ) : null}
    </div>
  );
}
